import { useMemo } from "react";
import { ALL_TIERS, TierNameValue } from "@ugg/shared/utils/tier-helpers";
import { asArray } from "@ugg/shared/utils/safe-coerce";
import { AUGMENT_RARITY_ALL } from "@ugg/shared/query-params/filter-options/aram-mayhem";
import { AugmentManifest, AugmentRarity, useAramMayhemAugmentByRarityTierList } from "@ugg/shared/hooks/aram-mayhem";

// Augment filtering shared by every ARAM Mayhem surface that lists augments:
// the by-rarity tier list page and the per-champion augments module on the
// champion build page.
//
// The CONTROLS are not here — `rarity` and `search` are ordinary query params
// now, so they render from the filter bars (FilterManager/filters/AugmentRarity
// and .../FilterSearch) like every other filter. What stays here is the part
// that is genuinely Mayhem-specific: the id → rarity index and the filter.

// Best-to-worst, matching the canonical RARITY_DISPLAY_ORDER on the Python
// side. Explicit (rather than Object.keys over the fetched data) so React's
// section order can't drift if upstream JSON key ordering changes.
export const RARITY_ORDER = ["kPrismatic", "kGold", "kSilver"] as const satisfies readonly AugmentRarity[];

// Human-readable labels, decoupled from the JSON's `k*` enum keys so display
// tweaks live here instead of at every render site.
export const RARITY_DISPLAY: Record<AugmentRarity, string> = {
  kPrismatic: "Prismatic",
  kGold: "Gold",
  kSilver: "Silver",
};

// URL value → wire key. The query param is the friendly form (`?rarity=gold`),
// the data is keyed `kGold`; see filter-options/aram-mayhem.ts.
const PARAM_TO_RARITY: Record<string, AugmentRarity> = {
  prismatic: "kPrismatic",
  gold: "kGold",
  silver: "kSilver",
};

// Resolve a `rarity` query value to a wire key, or undefined for "all" / an
// unrecognised value (validateQueryParams already drops values outside the
// option list, so undefined here means "don't filter").
export function resolveRarityParam(value: unknown): AugmentRarity | undefined {
  return typeof value === "string" && value !== AUGMENT_RARITY_ALL ? PARAM_TO_RARITY[value] : undefined;
}

// ─────────────────────────────────────────────────────────────────────
// id → rarity index
// ─────────────────────────────────────────────────────────────────────
//
// The per-champion augment feed is flat ({tier: [id]}) and the manifest is a
// bare id → name dict, so neither knows an augment's rarity. The by-rarity
// tier list is the only feed that carries it, so it doubles as the lookup
// table. Verified against live 16.15 data: 204 ranked augments, and every id
// in the per-champion feeds is present here (835 entries across 9 champions
// sampled, zero misses).
//
// Deliberately NOT server-rendered. The index exists only to answer a
// client-side interaction, and the server-rendered markup is whatever the URL
// asks for — so prefetching it would add a blocking SSR request to the champion
// build page. It resolves client-side and callers fall back to name-only
// filtering until it lands.
export function useAugmentRarityIndex(params: Record<string, any>, options?: { ssr?: boolean; skip?: boolean }) {
  const { data, loading, error } = useAramMayhemAugmentByRarityTierList({
    params,
    ssr: options?.ssr ?? false,
    skip: options?.skip,
  });

  const index = useMemo(() => {
    const result: Record<string, AugmentRarity> = {};
    const rarities = data?.rarities;
    if (!rarities) return result;
    for (const rarity of RARITY_ORDER) {
      const tiers = rarities[rarity]?.tiers;
      if (!tiers) continue;
      for (const tier of ALL_TIERS) {
        for (const augmentId of asArray(tiers[tier])) result[String(augmentId)] = rarity;
      }
    }
    return result;
  }, [data]);

  return { index, loading, error };
}

// ─────────────────────────────────────────────────────────────────────
// Filtering
// ─────────────────────────────────────────────────────────────────────

interface FilterAugmentTiersOptions {
  manifest: AugmentManifest;
  query: string;
  // Omit both to skip rarity filtering — the by-rarity page already renders one
  // section per rarity, so it drops whole sections instead of individual ids.
  rarity?: AugmentRarity;
  rarityIndex?: Record<string, AugmentRarity>;
}

// Narrow a {tier: [augmentId]} map by augment name and/or rarity. Returns a new
// map; tiers that end up empty stay as empty arrays and the tables already skip
// those, so no caller needs to prune.
export function filterAugmentTiers(
  tierlist: Partial<Record<TierNameValue, number[]>>,
  options: FilterAugmentTiersOptions,
): Partial<Record<TierNameValue, number[]>> {
  const { manifest, query, rarity, rarityIndex } = options;
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery && !rarity) return tierlist;

  const result: Partial<Record<TierNameValue, number[]>> = {};
  for (const tier of ALL_TIERS) {
    result[tier] = asArray(tierlist[tier]).filter((augmentId) => {
      if (rarity && rarityIndex?.[String(augmentId)] !== rarity) return false;
      if (!normalizedQuery) return true;
      const name = manifest[String(augmentId)];
      return typeof name === "string" && name.toLowerCase().includes(normalizedQuery);
    });
  }
  return result;
}
